import React, { useState, useEffect } from 'react';
import { 
  Palette, 
  Plus, 
  Trash2, 
  Edit3, 
  ArrowUp, 
  ArrowDown, 
  Eye, 
  Check, 
  Sparkles, 
  Smartphone, 
  Laptop, 
  Home, 
  Car, 
  Shirt, 
  Sun, 
  Flame, 
  BookOpen, 
  ShoppingBag 
} from 'lucide-react';
import { Modal } from '../common/Modal';
import { AdminApiClient } from '../../services/adminApiClient';

export interface LandingCategoryItem {
  id: string;
  name: string;
  slug: string;
  icon: string;
  bgColor: string; // Contrasting background pastel
  textColor: string;
  badge?: string;
  displayOrder: number;
  showOnHome: boolean;
  itemCount: number;
}

const PRESET_CONTRAST_COLORS = [
  { label: 'Sky Blue', hex: '#E0F2FE', text: '#0369A1' },
  { label: 'Amber Gold', hex: '#FEF3C7', text: '#B45309' },
  { label: 'Mint Green', hex: '#DCFCE7', text: '#15803D' },
  { label: 'Blush Pink', hex: '#FCE7F3', text: '#BE185D' },
  { label: 'Soft Purple', hex: '#EDE9FE', text: '#6D28D9' },
  { label: 'Warm Orange', hex: '#FFEDD5', text: '#C2410C' },
  { label: 'Teal Pulse', hex: '#CCFBF1', text: '#0F766E' },
  { label: 'Crimson Rose', hex: '#FEE2E2', text: '#B91C1C' },
];

const AVAILABLE_ICONS = [
  { id: 'phone', label: 'Phone', icon: Smartphone },
  { id: 'laptop', label: 'Electronics', icon: Laptop },
  { id: 'home', label: 'Home & Living', icon: Home },
  { id: 'car', label: 'Vehicles', icon: Car },
  { id: 'fashion', label: 'Fashion', icon: Shirt },
  { id: 'solar', label: 'Solar Power', icon: Sun },
  { id: 'deals', label: 'Hot Deals', icon: Flame },
  { id: 'books', label: 'Books & Media', icon: BookOpen },
  { id: 'other', label: 'General Goods', icon: ShoppingBag },
];

export const CategoryShowcaseManager: React.FC = () => {
  const [categories, setCategories] = useState<LandingCategoryItem[]>([
    {
      id: 'cat_phones',
      name: 'Phones & Tablets',
      slug: 'phones',
      icon: 'phone',
      bgColor: '#E0F2FE',
      textColor: '#0369A1',
      badge: 'TOP SELLER',
      displayOrder: 1,
      showOnHome: true,
      itemCount: 4120,
    },
    {
      id: 'cat_electronics',
      name: 'Electronics & Audio',
      slug: 'electronics',
      icon: 'laptop',
      bgColor: '#FEF3C7',
      textColor: '#B45309',
      badge: 'ESCROW 48H',
      displayOrder: 2,
      showOnHome: true,
      itemCount: 2840,
    },
    {
      id: 'cat_home',
      name: 'Home & Furniture',
      slug: 'furniture',
      icon: 'home',
      bgColor: '#DCFCE7',
      textColor: '#15803D',
      badge: '',
      displayOrder: 3,
      showOnHome: true,
      itemCount: 1950,
    },
    {
      id: 'cat_vehicles',
      name: 'Vehicles & Parts',
      slug: 'vehicles',
      icon: 'car',
      bgColor: '#EDE9FE',
      textColor: '#6D28D9',
      badge: 'INSPECTED',
      displayOrder: 4,
      showOnHome: true,
      itemCount: 890,
    },
    {
      id: 'cat_fashion',
      name: 'Fashion & Wear',
      slug: 'fashion',
      icon: 'fashion',
      bgColor: '#FCE7F3',
      textColor: '#BE185D',
      badge: 'THRIFT',
      displayOrder: 5,
      showOnHome: true,
      itemCount: 5600,
    },
    {
      id: 'cat_solar',
      name: 'Solar & Inverters',
      slug: 'solar',
      icon: 'solar',
      bgColor: '#FFEDD5',
      textColor: '#C2410C',
      badge: 'ZERO EMISSION',
      displayOrder: 6,
      showOnHome: true,
      itemCount: 740,
    },
    {
      id: 'cat_hot_deals',
      name: 'Live Deals & Clearance',
      slug: 'deals',
      icon: 'deals',
      bgColor: '#FEE2E2',
      textColor: '#B91C1C',
      badge: 'HOT 🔥',
      displayOrder: 7,
      showOnHome: true,
      itemCount: 1200,
    },
  ]);

  useEffect(() => {
    AdminApiClient.getPublicConfig()
      .then(config => {
        const saved = config?.configs?.category_showcase || config?.settings?.category_showcase;
        if (saved && Array.isArray(saved) && saved.length > 0) {
          setCategories(saved);
        }
      })
      .catch(err => console.warn('Could not fetch categories from API:', err));
  }, []);

  const persistCategories = async (updated: LandingCategoryItem[]) => {
    try {
      await AdminApiClient.updateSettings('category_showcase', updated);
    } catch (e: any) {
      console.error('Failed to persist categories to API:', e);
    }
  };

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<LandingCategoryItem | null>(null);
  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formIcon, setFormIcon] = useState('phone');
  const [formBgColor, setFormBgColor] = useState('#E0F2FE');
  const [formTextColor, setFormTextColor] = useState('#0369A1');
  const [formBadge, setFormBadge] = useState('');
  const [formShowOnHome, setFormShowOnHome] = useState(true);

  const handleOpenAdd = () => {
    setEditingCat(null);
    setFormName('');
    setFormSlug('');
    setFormIcon('phone');
    setFormBgColor('#E0F2FE');
    setFormTextColor('#0369A1');
    setFormBadge('');
    setFormShowOnHome(true);
    setModalOpen(true);
  };

  const handleOpenEdit = (item: LandingCategoryItem) => {
    setEditingCat(item);
    setFormName(item.name);
    setFormSlug(item.slug);
    setFormIcon(item.icon);
    setFormBgColor(item.bgColor);
    setFormTextColor(item.textColor);
    setFormBadge(item.badge || '');
    setFormShowOnHome(item.showOnHome);
    setModalOpen(true);
  };

  const handleSave = () => {
    if (!formName.trim()) return;
    const finalSlug = formSlug.trim() || formName.toLowerCase().replace(/\s+/g, '-');

    if (editingCat) {
      const updated = categories.map(c => {
        if (c.id === editingCat.id) {
          return {
            ...c,
            name: formName,
            slug: finalSlug,
            icon: formIcon,
            bgColor: formBgColor,
            textColor: formTextColor,
            badge: formBadge.trim() || undefined,
            showOnHome: formShowOnHome,
          };
        }
        return c;
      });
      setCategories(updated);
      persistCategories(updated);
    } else {
      const newItem: LandingCategoryItem = {
        id: `cat_${Date.now()}`,
        name: formName,
        slug: finalSlug,
        icon: formIcon,
        bgColor: formBgColor,
        textColor: formTextColor,
        badge: formBadge.trim() || undefined,
        displayOrder: categories.length + 1,
        showOnHome: formShowOnHome,
        itemCount: 0,
      };
      const updated = [...categories, newItem];
      setCategories(updated);
      persistCategories(updated);
    }

    setModalOpen(false);
  };

  const handleDelete = (id: string) => {
    const updated = categories.filter(c => c.id !== id);
    setCategories(updated);
    persistCategories(updated);
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === categories.length - 1) return;

    const nextIndex = direction === 'up' ? index - 1 : index + 1;
    const updated = [...categories];
    const temp = updated[index];
    updated[index] = updated[nextIndex];
    updated[nextIndex] = temp;

    // re-index display orders
    const reordered = updated.map((c, i) => ({ ...c, displayOrder: i + 1 }));
    setCategories(reordered);
    persistCategories(reordered);
  };

  const getIconComponent = (iconId: string) => {
    const found = AVAILABLE_ICONS.find(i => i.id === iconId);
    return found ? found.icon : ShoppingBag;
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border bg-[#152a23] dark:bg-[#152a23] border-[#22493c] light:bg-white light:border-[#D5E4DC]">
        <div>
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-brand-400" />
            <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
              Landing Page Category Showcase & Color Picker
            </h4>
          </div>
          <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-1">
            Add showcase product category with a vibrant, contrasting pastel background for maximum eye-catching contrast on both Web and Mobile apps.
          </p>
        </div>

        <button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>Add Showcase Category</span>
        </button>
      </div>

      {/* Categories Grid Table */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {categories.map((cat, idx) => {
          const IconComp = getIconComponent(cat.icon);

          return (
            <div
              key={cat.id}
              className="rounded-3xl p-5 border transition-all flex flex-col justify-between
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
                light:bg-white light:border-[#D5E4DC] light:shadow-sm"
            >
              <div>
                {/* Live Card Preview Box */}
                <div
                  className="rounded-2xl p-4 flex items-center justify-between transition-transform"
                  style={{ backgroundColor: cat.bgColor }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm"
                      style={{ backgroundColor: 'rgba(255,255,255,0.75)', color: cat.textColor }}
                    >
                      <IconComp className="w-6 h-6" />
                    </div>
                    <div>
                      <h5 className="font-bold text-sm" style={{ color: cat.textColor }}>
                        {cat.name}
                      </h5>
                      <span className="text-[11px] font-semibold opacity-80" style={{ color: cat.textColor }}>
                        {cat.itemCount.toLocaleString()} pre-loved items
                      </span>
                    </div>
                  </div>

                  {cat.badge && (
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide shadow-xs"
                      style={{ backgroundColor: cat.textColor, color: '#ffffff' }}
                    >
                      {cat.badge}
                    </span>
                  )}
                </div>

                {/* Metadata details */}
                <div className="mt-4 space-y-2 text-xs">
                  <div className="flex items-center justify-between text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                    <span>Target Deeplink:</span>
                    <span className="font-mono text-white dark:text-white light:text-[#0D1C17]">/market?category={cat.slug}</span>
                  </div>
                  <div className="flex items-center justify-between text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                    <span>Background Hex:</span>
                    <div className="flex items-center gap-1.5 font-mono text-white dark:text-white light:text-[#0D1C17]">
                      <span className="w-3.5 h-3.5 rounded-full border border-white/20" style={{ backgroundColor: cat.bgColor }}></span>
                      <span>{cat.bgColor}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                    <span>Show on Homepage:</span>
                    <span className={cat.showOnHome ? 'text-brand-400 font-bold' : 'text-slate-400 font-medium'}>
                      {cat.showOnHome ? 'Active (Visible)' : 'Hidden'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions Bar */}
              <div className="mt-5 pt-4 border-t border-[#22493c] dark:border-[#22493c] light:border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleMove(idx, 'up')}
                    disabled={idx === 0}
                    className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] disabled:opacity-30"
                    title="Move Order Up"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleMove(idx, 'down')}
                    disabled={idx === categories.length - 1}
                    className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] disabled:opacity-30"
                    title="Move Order Down"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-[11px] text-[#90cbb7] ml-1 font-mono font-bold">#{cat.displayOrder}</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenEdit(cat)}
                    className="p-1.5 rounded-lg text-brand-400 hover:bg-brand-500/15"
                    title="Edit Category & Color"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(cat.id)}
                    className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-500/15"
                    title="Delete Category"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit / Add Modal with Color Picker */}
      {modalOpen && (
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editingCat ? 'Edit Showcase Category' : 'Create Showcase Category'}
          subtitle="Customize category name, icon, and contrasting background color"
          maxWidth="lg"
        >
          <div className="space-y-4">
            {/* Category Name & Slug */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                  Category Name:
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Solar & Clean Energy"
                  className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                  URL Slug:
                </label>
                <input
                  type="text"
                  value={formSlug}
                  onChange={(e) => setFormSlug(e.target.value)}
                  placeholder="e.g. solar"
                  className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
                />
              </div>
            </div>

            {/* Icon Selector */}
            <div>
              <label className="block text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Select Category Icon:
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {AVAILABLE_ICONS.map((ico) => {
                  const Icon = ico.icon;
                  const isSelected = formIcon === ico.id;
                  return (
                    <button
                      key={ico.id}
                      type="button"
                      onClick={() => setFormIcon(ico.id)}
                      className={`p-2.5 rounded-xl border flex flex-col items-center gap-1 text-xs transition-all ${
                        isSelected
                          ? 'bg-brand-500/20 border-brand-500 text-brand-400 font-bold'
                          : 'bg-[#10231d] border-[#22493c] text-[#90cbb7] hover:border-brand-500/50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-[11px] truncate w-full text-center">{ico.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* CONTRASTING COLOR PICKER */}
            <div>
              <label className="block text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Contrasting Background Color:
              </label>

              {/* Preset Swatches */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-3">
                {PRESET_CONTRAST_COLORS.map((swatch) => {
                  const isSelected = formBgColor.toLowerCase() === swatch.hex.toLowerCase();
                  return (
                    <button
                      key={swatch.hex}
                      type="button"
                      onClick={() => {
                        setFormBgColor(swatch.hex);
                        setFormTextColor(swatch.text);
                      }}
                      className={`p-2.5 rounded-xl border text-left flex items-center justify-between transition-all ${
                        isSelected ? 'ring-2 ring-brand-500 border-white' : 'border-black/10 hover:opacity-95'
                      }`}
                      style={{ backgroundColor: swatch.hex }}
                    >
                      <span className="text-xs font-bold" style={{ color: swatch.text }}>
                        {swatch.label}
                      </span>
                      {isSelected && <Check className="w-4 h-4" style={{ color: swatch.text }} />}
                    </button>
                  );
                })}
              </div>

              {/* Custom Native Color Picker */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-[#10231d] border border-[#22493c]">
                <input
                  type="color"
                  value={formBgColor}
                  onChange={(e) => setFormBgColor(e.target.value)}
                  className="w-9 h-9 rounded-lg border-0 cursor-pointer bg-transparent"
                />
                <div className="flex-1">
                  <span className="text-xs text-[#90cbb7] block">Custom Background Hex:</span>
                  <input
                    type="text"
                    value={formBgColor}
                    onChange={(e) => setFormBgColor(e.target.value)}
                    className="w-28 mt-0.5 px-2 py-1 text-xs font-mono rounded bg-black/30 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <span className="text-xs text-[#90cbb7] block">Text Hex:</span>
                  <input
                    type="color"
                    value={formTextColor}
                    onChange={(e) => setFormTextColor(e.target.value)}
                    className="w-9 h-9 rounded-lg border-0 cursor-pointer bg-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Optional Promo Badge & Toggle */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                  Promo Badge (Optional):
                </label>
                <input
                  type="text"
                  value={formBadge}
                  onChange={(e) => setFormBadge(e.target.value)}
                  placeholder="e.g. HOT DEALS or 30% OFF"
                  className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
                />
              </div>

              <div className="flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  id="showOnHome"
                  checked={formShowOnHome}
                  onChange={(e) => setFormShowOnHome(e.target.checked)}
                  className="w-4 h-4 rounded text-brand-500 accent-brand-500"
                />
                <label htmlFor="showOnHome" className="text-xs font-semibold text-white cursor-pointer">
                  Display in Mobile & Web Home Showcase
                </label>
              </div>
            </div>

            {/* Live Interactive Preview */}
            <div className="pt-2">
              <span className="text-xs text-[#90cbb7] font-semibold block mb-1">Live Card Preview:</span>
              <div
                className="rounded-2xl p-4 flex items-center justify-between"
                style={{ backgroundColor: formBgColor }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shadow-xs"
                    style={{ backgroundColor: 'rgba(255,255,255,0.75)', color: formTextColor }}
                  >
                    {React.createElement(getIconComponent(formIcon), { className: 'w-5 h-5' })}
                  </div>
                  <div>
                    <h5 className="font-bold text-sm" style={{ color: formTextColor }}>
                      {formName || 'Category Name'}
                    </h5>
                    <span className="text-[11px] font-medium opacity-80" style={{ color: formTextColor }}>
                      Interactive preview
                    </span>
                  </div>
                </div>

                {formBadge && (
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase shadow-xs"
                    style={{ backgroundColor: formTextColor, color: '#ffffff' }}
                  >
                    {formBadge}
                  </span>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#22493c]">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!formName.trim()}
                className="px-5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 disabled:opacity-50 shadow-glow-sm"
              >
                Save Category
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
